import { useTranslation } from "react-i18next";
import {
  Col,
  Flex,
  Row,
  Spin,
  Table,
  TableColumnsType,
  TableProps,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import PageHeader from "../../../../components/page-header/PageHeader";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { getCourseName, getTranscript } from "../../../../service/courses";
import { CourseTranscriptInfo } from "../../../../types/course";
import {
  LearningStatus,
  StudentCourseResult,
} from "../../../../constants/course";
import { BlockMath } from "react-katex";
import { convertToLatex } from "../../../../utils/functionUltils";
import useKeyword from "../../../../hooks/useKeyword";
import "./style.scss";
import { toNumber } from "lodash";
import { formatNumber } from "../../../../utils/format";
import ViewDetailTest from "./ViewDetailTest";
import { useParams } from "react-router-dom";
import TableData from "../../../../components/table-data/TableData";

const { Paragraph } = Typography;
type Props = {};

function ScoreBoard(props: Props) {
  const { t } = useTranslation();
  const { studentId } = useParams();

  const [scoreData, setScoreData] = useState<CourseTranscriptInfo>();
  const [loading, setLoading] = useState<boolean>(false);
  const courseInteractiveData = scoreData?.courseInteractiveData;
  const examInfoDTOS = scoreData?.examInfoDTOS;
  const { courseId } = useParams();
  const [detailData, setDetailData] = useState<any>(null);
  const formulaText = convertToLatex(courseInteractiveData?.formulaQuiz);
  const [statusOrder, setStatusOrder] = useState<string>("");
  const [fieldSortOrder, setFieldSortOrder] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    const getDetailCourseData = async () => {
      try {
        await getCourseName(Number(courseId)).then((res) => {
          const { data } = res.data;
          setDetailData(data);
        });
      } catch (error) {}
    };
    getDetailCourseData();
  }, [courseId]);

  useEffect(() => {
    const getScoreBoard = async () => {
      try {
        setLoading(true);
        const res = await getTranscript({
          studentId,
          courseId: courseId,
        });
        setScoreData(res.data.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    getScoreBoard();
  }, [studentId, courseId]);

  const listBreadcrumb = [
    {
      title: t("Quản lý khóa học", "Quản lý khóa học"),
    },
    {
      title: <span>{detailData?.name}</span>,
    },
    {
      title: <span>{t("Bảng điểm", "Bảng điểm")}</span>,
    },
  ];

  const columns: TableColumnsType<any> = [
    {
      title: t("Bài đánh giá", "Bài đánh giá"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("Điểm tối thiểu", "Điểm tối thiểu"),
      dataIndex: "courseScore",
      key: "courseScore",
      render(value, record, index) {
        const distBlock = courseInteractiveData?.achievementMaterials?.find(
          (item: any) => item.blockId === record?.blockId
        );
        if (distBlock) return <>{formatNumber(distBlock?.courseScore)}</>;
        return <>{"-"}</>;
      },
    },
    {
      title: t("Điểm ghi nhận", "Điểm ghi nhận"),
      dataIndex: "result",
      key: "result",
      render(value, record, index) {
        return <>{value ? value : "-"}</>;
      },
    },
    {
      key: "action",
      title: t("Lịch sử điểm", "Lịch sử điểm"),
      dataIndex: "action",

      render: (value, record) => (
        <>
          <ViewDetailTest
            data={record}
            studentId={Number(studentId)}
            courseName={scoreData?.courseName}
          />
        </>
      ),
    },
  ];

  const displayLearningStatus = (code: number | undefined) => {
    if (!code) return t("Không có thông tin", "Không có thông tin");
    switch (code) {
      case LearningStatus.INPROGRESS:
        return <div className="status-wrapper status-info">{t("Đang học", "Đang học")}</div>;
      case LearningStatus.DONE:
        return (
          <div className="status-wrapper status-success">{t("Đã hoàn thành", "Đã hoàn thành")}</div>
        );
      default:
        return (
          <div className="status-wrapper status-info">{t("Không có thông tin", "Không có thông tin")}</div>
        );
    }
  };

  const displayUnitStatus = (isPass: boolean | null) => {
    switch (isPass) {
      case true:
        return (
          <Flex align="center" gap={4}>
            <CheckCircleFilled style={{ color: "#52c41a" }} />
            {t("Đạt", "Đạt")}
          </Flex>
        );
      case false:
        return (
          <Flex align="center" gap={4}>
            <CloseCircleFilled style={{ color: "#FF4D4F" }} />
            {t("Chưa đạt", "Chưa đạt")}
          </Flex>
        );
      case null:
        return (
          <Flex align="center" gap={4}>
            <CheckCircleFilled style={{ color: "#98A2B3" }} />
            {t("Chưa có kết quả", "Chưa có kết quả")}
          </Flex>
        );
      default:
        return (
          <Flex align="center" gap={4}>
            <CheckCircleFilled style={{ color: "#98A2B3" }} />
            {t("Chưa có kết quả", "Chưa có kết quả")}
          </Flex>
        );
    }
  };

  const displayCourseFinalStatus = (resultCourse: number | undefined) => {
    switch (resultCourse) {
      case StudentCourseResult.FAIL:
        return <div className="status-wrapper status-info">{t("Chưa đạt", "Chưa đạt")}</div>;
      case StudentCourseResult.PASS:
        return <div className="status-wrapper status-success">{t("Đạt", "Đạt")}</div>;
      default:
        return <div className="status-wrapper status-info">{t("Đang cập nhật", "Đang cập nhật")}</div>;
    }
  };

  // Sort
  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const data =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : "";
    setStatusOrder(data);
    setFieldSortOrder(sorter.field);
  };

  return (
    <div>
      <Spin spinning={loading}>
        <div className="page-header-group student-learning-progress">
          <div className={`page-header-group--layout`}>
            <div className="table-header--wrapper justify-between pb-6">
              <div className="table-header--left">
                <PageHeader
                  title={t("Chi tiết bảng điểm", "Chi tiết bảng điểm")}
                  listBreadcrumb={listBreadcrumb}
                  breadcrumb={true}
                />
              </div>
            </div>
          </div>
        </div>{" "}
        <div className="score-board-content">
          <Row gutter={[24, 24]} className="mb-3">
            <Col xs={24} md={8}>
              <Flex align="center" gap={62} wrap>
                <img
                  className="user-avatar"
                  src={scoreData?.avatar || "/assets/icons/empty-course.png"}
                  alt="student avatar"
                />
                <div>
                  <Typography className="mt-1">
                    {t("Học viên:", "Học viên:")}{" "}
                    <span className="text-normal ml-1">
                      {scoreData?.studentName}
                    </span>
                  </Typography>
                  <Typography className="mt-1">
                    Email:{" "}
                    <span className="text-normal ml-1">{scoreData?.email}</span>
                  </Typography>

                  <Flex gap={8} align="center" className="mt-1">
                    <Typography>{t("Trạng thái học tập:", "Trạng thái học tập:")} </Typography>

                    {displayLearningStatus(scoreData?.learningStatus)}
                  </Flex>
                </div>
              </Flex>
            </Col>
            <Col xs={24} md={16}>
              <Flex align="center" gap={8}>
                <Typography>{t("Kết quả học tập:", "Kết quả học tập:")} </Typography>
                <Flex gap={16}>
                  {displayCourseFinalStatus(scoreData?.resultCourse)}
                </Flex>
                <Typography>GPA: </Typography>
                <Flex gap={16}>{formatNumber(scoreData?.gpa)}</Flex>
              </Flex>
              <div className="completion-condition">
                <Typography>{t("Điều kiện hoàn thành", "Điều kiện hoàn thành")} </Typography>

                <div className="font-size-14 mt-1 text-primary">
                  <Flex align="center" gap={8}>
                    <span>
                      {t("* Hoàn thành", "* Hoàn thành")}{" "}
                      {`${
                        courseInteractiveData?.minimumPercentageComplete || 0
                      }% học liệu.`}
                    </span>
                    {toNumber(scoreData?.completedPercentage) >=
                    toNumber(
                      scoreData?.courseInteractiveData
                        ?.minimumPercentageComplete
                    ) ? (
                      <Flex align="center" gap={4}>
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                        {t("Đạt", "Đạt")}
                      </Flex>
                    ) : (
                      <Flex align="center" gap={4}>
                        <CloseCircleFilled style={{ color: "#FF4D4F" }} />
                        {t("Chưa đạt", "Chưa đạt")}
                      </Flex>
                    )}
                  </Flex>

                  {toNumber(courseInteractiveData?.minimumPercentageComplete) <
                    100 &&
                  courseInteractiveData?.regularMaterials?.length > 0 ? (
                    <div className="mt-1">
                      {t("* Các học liệu bắt buộc:", "* Các học liệu bắt buộc:")}
                      <Row gutter={12}>
                        {courseInteractiveData?.regularMaterials
                          ?.filter((item: any) => item?.requireToComplete === 1)
                          ?.map((item: any) => (
                            <Col key={item?.id} xs={24} md={24} xl={12}>
                              <Flex align="center" gap={12}>
                                <div className="font-weight-4 text-black">
                                  - {item?.name}
                                </div>
                                <div>
                                  {item?.isPass ? (
                                    <Flex align="center" gap={4}>
                                      <CheckCircleFilled
                                        style={{ color: "#52c41a" }}
                                      />
                                      {t("Đạt", "Đạt")}
                                    </Flex>
                                  ) : (
                                    <Flex align="center" gap={4}>
                                      <CloseCircleFilled
                                        style={{ color: "#FF4D4F" }}
                                      />
                                      {t("Chưa đạt", "Chưa đạt")}
                                    </Flex>
                                  )}
                                </div>
                              </Flex>
                            </Col>
                          ))}
                      </Row>
                    </div>
                  ) : null}
                </div>
                <div className="">
                  {courseInteractiveData?.achievementMaterials?.length > 0 ? (
                    <div className="font-size-14 text-primary mt-1">
                      {t("* Đạt điểm tối thiểu của các bài kiểm tra bắt buộc:", "* Đạt điểm tối thiểu của các bài kiểm tra bắt buộc:")}{" "}
                      <Row gutter={12}>
                        {courseInteractiveData?.achievementMaterials?.map(
                          (item: any, index: number) => {
                            const currentUnitDto = examInfoDTOS?.find(
                              (unit: any) => unit?.blockId === item?.blockId
                            );
                            return (
                              <Col key={index} xs={24} md={24} xl={12}>
                                <Flex align="center" gap={12}>
                                  <div className="font-weight-4 text-black">
                                    {" "}
                                    - {item?.unitName}:{" "}
                                    {`${formatNumber(item?.courseScore)} điểm`}
                                  </div>
                                  {displayUnitStatus(currentUnitDto?.isPass)}
                                </Flex>
                              </Col>
                            );
                          }
                        )}
                      </Row>
                    </div>
                  ) : null}
                  <div className="font-size-14 mt-1">
                    <Flex
                      className="text-black font-weight-4 text-primary"
                      align="center"
                      gap={5}
                    >
                      {t("* Điểm GPA tối thiểu cần đạt:", "* Điểm GPA tối thiểu cần đạt:")}{" "}
                      <span className="text-black">
                        {formatNumber(courseInteractiveData?.minimumGPA)}
                      </span>
                      {courseInteractiveData?.minimumGPA <=
                        toNumber(scoreData?.gpa) ? (
                        <Flex align="center" gap={8}>
                          <CheckCircleFilled style={{ color: "#52c41a" }} />
                          {t("Đạt", "Đạt")}
                        </Flex>
                      ) : (
                        <Flex align="center" gap={8}>
                          <CloseCircleFilled style={{ color: "#FF4D4F" }} />
                          {t("Chưa đạt", "Chưa đạt")}
                        </Flex>
                      )}
                    </Flex>
                    <Flex align="center" gap={12}>
                      <Paragraph>{` (GPA`} </Paragraph> <Paragraph>=</Paragraph>
                      <Paragraph className="text-14">
                        {formulaText ? (
                          <BlockMath
                            math={convertToLatex(
                              courseInteractiveData?.formulaQuiz
                            )}
                          />
                        ) : (
                          `<Công thức>`
                        )}
                      </Paragraph>
                      <Paragraph>{`)`}</Paragraph>
                    </Flex>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <TableData
            dataTable={examInfoDTOS}
            dataColumns={columns}
            pagination={true}
            pageSize={pageSize}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            totalRecords={examInfoDTOS?.length}
            handleTableChangeProps={handleTableChange}
          />
        </div>
      </Spin>
    </div>
  );
}

export default ScoreBoard;
